import { Dumbbell, BarChart3, Smartphone, ShieldCheck } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useSearchParams } from 'react-router-dom';

export function LoginPage() {
  const { login } = useAuth();
  const [searchParams] = useSearchParams();
  const error = searchParams.get('error');

  const errorMessages: Record<string, string> = {
    oauth_denied: 'Kirjautuminen peruutettiin',
    invalid_state: 'Virheellinen sessio, yritä uudelleen',
    token_error: 'Kirjautuminen epäonnistui, yritä uudelleen',
    server_error: 'Palvelinvirhe, yritä myöhemmin uudelleen',
    access_denied: 'Pääsy estetty. Tämä sovellus on vain kutsuttujen käyttäjien käytössä.',
  };

  return (
    <main className="min-h-screen bg-linear-to-br from-primary-500 to-primary-700 flex items-center justify-center px-4">
      <a
        href="#login-form"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-100 focus:px-4 focus:py-2 focus:bg-white focus:text-primary-700 focus:rounded-lg focus:outline-hidden focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-primary-600"
      >
        Siirry kirjautumiseen
      </a>
      <div className="max-w-md w-full">
        {/* Logo ja otsikko */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-white rounded-2xl shadow-lg mb-4">
            <Dumbbell className="h-10 w-10 text-primary-600" />
          </div>
          <h1 className="text-3xl font-bold text-white">Treenipäiväkirja</h1>
          <p className="text-white/90 mt-2">
            Seuraa salitreenejäsi ja kehitystäsi
          </p>
        </div>

        {/* Kirjautumiskortti */}
        <div id="login-form" className="card p-8" tabIndex={-1}>
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {errorMessages[error] || 'Tuntematon virhe tapahtui'}
            </div>
          )}

          <div className="text-center mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Tervetuloa!
            </h2>
            <p className="text-gray-500 mt-1">
              Kirjaudu sisään Google-tilillä aloittaaksesi
            </p>
          </div>

          <button
            onClick={login}
            aria-label="Kirjaudu Google-tilillä"
            className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-border-strong rounded-lg bg-surface text-text font-medium hover:bg-surface-2 transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Kirjaudu Google-tilillä
          </button>

          <p className="mt-6 text-xs text-gray-500 text-center">
            Kirjautumalla hyväksyt palvelun käyttöehdot ja tietosuojakäytännön.
          </p>
        </div>

        {/* Ominaisuudet */}
        <div className="mt-8 grid grid-cols-3 gap-4 text-center text-white">
          <div>
            <BarChart3 className="h-7 w-7 mx-auto" aria-hidden="true" />
            <p className="text-sm mt-1 text-white/90">Tilastot</p>
          </div>
          <div>
            <Smartphone className="h-7 w-7 mx-auto" aria-hidden="true" />
            <p className="text-sm mt-1 text-white/90">Mobiili</p>
          </div>
          <div>
            <ShieldCheck className="h-7 w-7 mx-auto" aria-hidden="true" />
            <p className="text-sm mt-1 text-white/90">Turvallinen</p>
          </div>
        </div>

        <p className="mt-8 text-center text-xs text-white/70">
          <a href="https://arkisankari.fi" className="hover:text-white underline-offset-2 hover:underline">
            Osa arkisankari.fi:tä
          </a>
        </p>
      </div>
    </main>
  );
}
